import { ToastContext } from "context/ToastContext";
import { useContext, useState } from "react";
import {
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreview,
  IDefaultRulePropagationPreviewResponse,
} from "types/DefaultRules/DefaultRules";

import PropagateDefaultModal from "../../PayerRules/components/PropagateDefaultModal";

type LazyPreviewHook = () => [
  (id: string) => {
    unwrap: () => Promise<IDefaultRulePropagationPreviewResponse>;
  },
  unknown
];

type ApplyHook = () => [
  (arg: { id: string; requests: IDefaultRulePropagationApplyRequest[] }) => {
    unwrap: () => Promise<IDefaultRulePropagationApplyResponse>;
  },
  { isLoading: boolean }
];

type Params = {
  useLazyPreview: LazyPreviewHook;
  useApplyPropagation: ApplyHook;
  /** Runs when the flow is finished — either because there were no copies,
   *  the user cancelled, the apply succeeded, or it errored. Typically navigates
   *  away from the edit page. */
  onDone: () => void;
};

type DialogState = {
  id: string;
  isLoading: boolean;
  preview?: IDefaultRulePropagationPreview;
};

/**
 * After saving an edit to a default rule, opens a modal showing the propagation
 * preview so the user can push the changes to linked copies. If the default has
 * no linked copies, the flow short-circuits straight to `onDone`.
 */
export function usePropagateAfterEdit({
  useLazyPreview,
  useApplyPropagation,
  onDone,
}: Params) {
  const { current: toast } = useContext(ToastContext);
  const [fetchPreview] = useLazyPreview();
  const [apply, { isLoading: isApplying }] = useApplyPropagation();
  const [state, setState] = useState<DialogState | null>(null);

  const openForId = async (id: string) => {
    setState({ id, isLoading: true });
    try {
      const result = await fetchPreview(id).unwrap();
      if (result.data.total === 0) {
        setState(null);
        onDone();
        return;
      }
      setState({ id, isLoading: false, preview: result.data });
    } catch (_e) {
      toast?.show({
        severity: "error",
        summary: "Error",
        detail: "Unable to load propagation preview.",
      });
      setState(null);
      onDone();
    }
  };

  const modal = state ? (
    <PropagateDefaultModal
      visible
      onHide={() => {
        setState(null);
        onDone();
      }}
      title="Propagate changes to linked copies?"
      preview={state.preview}
      isLoading={state.isLoading}
      isApplying={isApplying}
      onApply={async (requests) => {
        try {
          const result = await apply({ id: state.id, requests }).unwrap();
          const { updated, skipped, conflicts } = result.data;
          const parts = [
            `Updated ${updated}`,
            skipped > 0 ? `skipped ${skipped}` : null,
            conflicts > 0 ? `conflicts ${conflicts}` : null,
          ].filter(Boolean);
          toast?.show({
            severity: conflicts > 0 ? "warn" : "success",
            summary: conflicts > 0 ? "Propagated with conflicts" : "Propagated",
            detail:
              conflicts > 0
                ? `${parts.join(
                    "; "
                  )}. Some copies diverged since preview — re-open edit to retry.`
                : parts.join("; "),
            life: conflicts > 0 ? 8000 : 4000,
          });
          setState(null);
          onDone();
        } catch (_e) {
          toast?.show({
            severity: "error",
            summary: "Error",
            detail: "Propagation failed.",
          });
        }
      }}
    />
  ) : null;

  return { openForId, modal };
}
